import { useCallback, useEffect, useRef, useState } from "react";
import {
  NewRequestFormProps,
  SocialMediaPostFields,
  SocialMediaPromotionType,
} from "../../types/request.types";
import { useForm, UseFormReturnType } from "@mantine/form";
import { Editor } from "@tiptap/react";
import {
  Group,
  Input,
  MultiSelect,
  MultiSelectProps,
  NativeSelect,
  TextInput,
} from "@mantine/core";
import FormContacts from "../contacts/FormContacts";
import ContentEditor from "../ContentEditor";
import FormAttachments from "../attachments/FormAttachments";
import { DatePickerInput } from "@mantine/dates";
import platformIcons, { PlatformIconMapping } from "../icons/platformIcons";
import { useConvertFrom } from "../../hooks/use-convert-from";
import { ActionBar } from "./ActionBar";

const renderSocialMediaPlatform: MultiSelectProps['renderOption'] = ({ option }) => {
  const { value, label } = option;
  const Icon = platformIcons[value as keyof PlatformIconMapping];

  return (
    <Group gap="xs">
      <Icon size={16} />
      <span>{label}</span>
    </Group>
  );
}

export default function SocialMediaPostForm(props: NewRequestFormProps) {
  const { onSubmit, tempId, fromRequest } = props;
  const descEditorRef = useRef<Editor>(null);
  const linksEditorRef = useRef<Editor>(null);
  const form = useForm<SocialMediaPostFields>({
    mode: 'uncontrolled',
    initialValues: {
      name: '',
      description: '',
      links: '',
      platforms: [],
      promotion_type: 'organic',
      budget_code: '',
      requested_completion_date: null,
    },
    validate: {
      description: (val) => (
        descEditorRef.current && descEditorRef.current.getText().length > 0 ?
          null : 'Please fill out this field'
      ),
      requested_completion_date: (val) => (
        val ? null : 'Please fill out this field'
      ),
      budget_code: (val, values) => {
        if (values.promotion_type !== 'organic' && (!val || val.trim().length === 0))
          return 'Please fill out this field';

        return null;
      }
    }
  });
  const [promoType, setPromoType] = useState<SocialMediaPromotionType>('organic');
  const promoProps = form.getInputProps('promotion_type');
  useConvertFrom<SocialMediaPostFields>(form, descEditorRef.current, fromRequest);

  const onFormSubmit = useCallback((submitVals: SocialMediaPostFields) => {
    const newRequest = { ...submitVals };
    newRequest.description = descEditorRef.current?.getHTML();
    newRequest.links = linksEditorRef.current?.getHTML();
    onSubmit(newRequest);
  }, [onSubmit]);

  return (
    <form onSubmit={form.onSubmit(onFormSubmit)}>
      {!fromRequest && (
        <Input.Wrapper
          label="Contacts"
          mt="md"
        >
          <FormContacts<SocialMediaPostFields> form={form} />
        </Input.Wrapper>
      )}

      <TextInput
        {...form.getInputProps('name')}
        key={form.key('name')}
        label="Project name"
        mt="md"
        required
      />

      <ContentEditor<SocialMediaPostFields>
        formKey="description"
        label="Description"
        form={form} 
        editorRef={descEditorRef}
        required
        mt="md"
      />

      <ContentEditor<SocialMediaPostFields>
        formKey="links"
        label="Links/Calls to action"
        form={form} 
        editorRef={linksEditorRef}
        mt="md"
      />

      <NativeSelect
        {...form.getInputProps('promotion_type')}
        key={form.key('promotion_type')}
        onChange={(event) => {
          setPromoType(event.currentTarget.value as SocialMediaPromotionType);
          promoProps.onChange?.(event);
        }}
        label="Promotion type"
        mt="md"
        data={[
          { value: 'organic', label: 'Organic' },
          { value: 'boosted', label: 'Boosted' },
          { value: 'ad', label: 'Ad' },
        ]}
      />

      {promoType !== 'organic' && (
        <TextInput
          {...form.getInputProps('budget_code')}
          key={form.key('budget_code')}
          label="Budget code"
          mt="md"
        />
      )}

      <MultiSelect
        {...form.getInputProps('platforms')}
        key={form.key('platforms')}
        label="Platforms"
        placeholder="Select all that apply"
        mt="md"
        renderOption={renderSocialMediaPlatform}
        data={[
          { value: 'facebook', label: 'Facebook' },
          { value: 'instagram', label: 'Instagram' },
          { value: 'x', label: 'X (Twitter)' },
          { value: 'linkedin', label: 'LinkedIn' },
        ]}
      />

      <DatePickerInput
        {...form.getInputProps('requested_completion_date')}
        key={form.key('requested_completion_date')}
        label="Requested completion date"
        required
        highlightToday
        mt="md"
      />

      {!fromRequest && (
        <Input.Wrapper
          label="Attachments"
          mt="md"
        >
          <FormAttachments tempId={tempId} />
        </Input.Wrapper>
      )}

      <ActionBar showConvertNotice={!!fromRequest} />
    </form>
  )
}
